import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Fingerprint } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { ThemeToggle, LanguageSelector } from '../../components/Controls';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { api, errMsg } from '../../lib/api';
import { loginWithPasskey, passkeySupported } from '../../lib/passkeys';

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-vj-obsidian text-vj-silver flex flex-col vj-grid-bg">
      <div className="flex items-center justify-between p-5">
        <Logo size={34} />
        <div className="flex items-center gap-2"><LanguageSelector /><ThemeToggle /></div>
      </div>
      <div className="flex-1 grid place-items-center px-4 py-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-vj-grey hover:text-vj-neon mb-6"><ArrowLeft size={14} /> Back to home</Link>
          <div className="vj-card p-7 lg:p-8">
            <h1 className="font-display text-2xl font-bold vj-gradient-text">{title}</h1>
            {subtitle && <p className="text-sm text-vj-grey mt-2">{subtitle}</p>}
            <div className="mt-6">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full h-11 px-3.5 rounded-lg bg-vj-charcoal border border-border text-foreground text-sm focus:outline-none focus:border-vj-neon/60 transition-colors';
const btnCls = 'w-full h-11 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm hover:-translate-y-0.5 transition-all vj-glow-green disabled:opacity-60 disabled:translate-y-0';

function PwField({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-vj-grey">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export function Login() {
  const { t } = useI18n();
  const { login, mfaVerify, completePasskey } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [code, setCode] = useState('');

  const go = (u) => navigate(u.role && u.role !== 'user' ? '/admin' : '/dashboard');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfaRequired) { setMfa(true); toast.message('Enter your 2FA code'); }
      else { toast.success('Welcome back'); go(res.user); }
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const u = await mfaVerify(code.trim()); toast.success('Verified'); go(u); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  const passkey = async () => {
    setLoading(true);
    try {
      const res = await loginWithPasskey(email || undefined);
      if (res.mfa_required) { setMfa(true); toast.message('Enter your 2FA code'); }
      else { const u = await completePasskey(); toast.success('Signed in with passkey'); go(u); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Passkey sign-in failed');
    } finally { setLoading(false); }
  };

  if (mfa) {
    return (
      <Shell title="Two-factor authentication" subtitle="Enter the 6-digit code from your authenticator app, or a recovery code.">
        <form onSubmit={verifyMfa} className="space-y-4">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={`${inputCls} font-mono tracking-widest text-center`} data-testid="mfa-code" autoFocus />
          <button disabled={loading} className={btnCls} data-testid="mfa-submit">{loading ? t('common.loading') : 'Verify'}</button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell title={t('auth.loginTitle')} subtitle="Access your VEYJEX trading account.">
      <form onSubmit={submit} className="space-y-4">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('common.email')} className={inputCls} data-testid="login-email" />
        <PwField value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('common.password')} testId="login-password" />
        <div className="flex justify-end"><Link to="/forgot-password" className="text-xs text-vj-neon">{t('auth.forgot')}</Link></div>
        <button disabled={loading} className={btnCls} data-testid="login-submit">{loading ? t('common.loading') : t('nav.login')}</button>
      </form>
      {passkeySupported() && (
        <>
          <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-border" /><span className="text-xs text-vj-grey">or</span><div className="flex-1 h-px bg-border" /></div>
          <button onClick={passkey} disabled={loading} className="w-full h-11 rounded-lg border border-vj-neon/40 text-vj-neon font-medium text-sm inline-flex items-center justify-center gap-2 hover:bg-vj-neon/10 transition-all" data-testid="login-passkey">
            <Fingerprint size={16} /> Sign in with a passkey
          </button>
        </>
      )}
      <p className="text-sm text-vj-grey mt-5 text-center">{t('auth.noAccount')} <Link to="/register" className="text-vj-neon">{t('nav.register')}</Link></p>
    </Shell>
  );
}

export function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', email: '', country: '', password: '', confirm_password: '' });
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm_password) return toast.error('Passwords do not match');
    if (!terms) return toast.error('Please accept the Terms and Privacy Policy');
    setLoading(true);
    try {
      await register({ ...f, accept_terms: true });
      toast.success('Account created');
      navigate('/onboarding');
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  return (
    <Shell title={t('auth.registerTitle')} subtitle="Join the VEYJEX Global Trading Exchange.">
      <form onSubmit={submit} className="space-y-4">
        <input required value={f.name} onChange={up('name')} placeholder={t('auth.fullName')} className={inputCls} data-testid="register-name" />
        <input type="email" required value={f.email} onChange={up('email')} placeholder={t('common.email')} className={inputCls} data-testid="register-email" />
        <input required value={f.country} onChange={up('country')} placeholder={t('auth.country')} className={inputCls} data-testid="register-country" />
        <PwField value={f.password} onChange={up('password')} placeholder={`${t('common.password')} (min 8 chars)`} testId="register-password" />
        <PwField value={f.confirm_password} onChange={up('confirm_password')} placeholder={t('auth.confirmPassword')} testId="register-confirm" />
        <label className="flex items-start gap-2 text-xs text-vj-grey cursor-pointer">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 accent-vj-neon" data-testid="register-terms" />
          <span>{t('auth.acceptTerms')}</span>
        </label>
        <button disabled={loading} className={btnCls} data-testid="register-submit">{loading ? t('common.loading') : t('nav.register')}</button>
      </form>
      <p className="text-sm text-vj-grey mt-5 text-center">{t('auth.hasAccount')} <Link to="/login" className="text-vj-neon">{t('nav.login')}</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSent(data.reset_token || 'sent');
      toast.success('If the email exists, a reset link was generated (dev mode).');
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Reset password" subtitle="We'll generate a secure reset link.">
      <form onSubmit={submit} className="space-y-4">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} data-testid="forgot-email" />
        <button className={btnCls} data-testid="forgot-submit">Send reset link</button>
      </form>
      {sent && sent !== 'sent' && (
        <div className="mt-4 text-xs text-vj-grey break-all">
          Dev reset link: <Link className="text-vj-neon" to={`/reset-password?token=${sent}`}>Open reset form</Link>
        </div>
      )}
      <p className="text-sm text-vj-grey mt-5 text-center"><Link to="/login" className="text-vj-neon">Back to login</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const token = params.get('token') || '';
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/reset-password', { token, password });
      toast.success('Password updated. Please log in.');
      navigate('/login');
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Set new password">
      <form onSubmit={submit} className="space-y-4">
        <PwField value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 chars)" testId="reset-password" />
        <button className={btnCls} data-testid="reset-submit">Update password</button>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  useEffect(() => {
    const token = params.get('token');
    if (!token) { setStatus('error'); return; }
    api.post('/auth/verify-email', { token }).then(() => setStatus('ok')).catch(() => setStatus('error'));
  }, [params]);
  return (
    <Shell title="Email verification">
      <p className="text-sm text-vj-grey">
        {status === 'verifying' && 'Verifying your email…'}
        {status === 'ok' && 'Your email has been verified. You can continue.'}
        {status === 'error' && 'This verification link is invalid or expired.'}
      </p>
      <Link to="/dashboard" className="inline-block mt-5 text-vj-neon text-sm">Go to dashboard</Link>
    </Shell>
  );
}
